import type { LucideIcon } from 'lucide-react';
import { Warehouse, Package, Box, TrendingUp, Users, Layers } from 'lucide-react';
import { Card, CardBody } from '@vnetwork/ui/primitives';
import { Stat } from '@vnetwork/ui/data';
import type { DashboardStats } from '@/api/capacities';

/**
 * Warehouse headline stat cards for the Physical Inventory dashboard.
 *
 * Mirrors the legacy render: a 6-up grid of bordered cards, each with a small
 * uppercase-ish title top-left, a large numeric value, a muted hint, and a
 * colored lucide icon in the top-right corner. Uses `Stat` for the
 * label/value/hint pattern; the corner icon is composed via a flex wrapper
 * since `Stat` has no icon slot.
 *
 * Icon colors use semantic solid tokens only (theme-reactive).
 */

interface StatCardDef {
  key: keyof DashboardStats;
  label: string;
  hint: string;
  icon: LucideIcon;
  /** Icon color — semantic solid token. */
  color: string;
}

const CARDS: StatCardDef[] = [
  {
    key: 'total_locations',
    label: 'Total Locations',
    hint: 'Warehouses & Datacenters',
    icon: Warehouse,
    color: 'var(--vn-primary-solid)',
  },
  {
    key: 'total_equipment',
    label: 'Total Equipment',
    hint: 'All equipment items',
    icon: Package,
    color: 'var(--vn-success-solid)',
  },
  {
    key: 'total_components',
    label: 'Total Components',
    hint: 'All component parts',
    icon: Box,
    color: 'var(--vn-accent-solid)',
  },
  {
    key: 'total_processes',
    label: 'Total Processes',
    hint: 'Import/Export/Transfer',
    icon: TrendingUp,
    color: 'var(--vn-warning-solid)',
  },
  {
    key: 'total_suppliers',
    label: 'Total Suppliers',
    hint: 'Active suppliers',
    icon: Users,
    color: 'var(--vn-danger-solid)',
  },
  {
    key: 'total_models',
    label: 'Item Models',
    hint: 'Model templates',
    icon: Layers,
    color: 'var(--vn-info-solid)',
  },
];

const GRID: React.CSSProperties = {
  display: 'grid',
  gap: 'var(--vn-space-4)',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
};

/** 6-up grid of warehouse headline metrics with colored corner icons. */
export function PhysicalStatCards({ data }: { data: DashboardStats }) {
  return (
    <div style={GRID}>
      {CARDS.map((c) => {
        const Icon = c.icon;
        return (
          <Card key={c.key} flat style={{ height: '100%' }}>
            <CardBody>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--vn-space-2)' }}>
                <Stat
                  label={c.label}
                  value={Number(data[c.key] ?? 0).toLocaleString()}
                  hint={c.hint}
                  style={{ flex: 1, minWidth: 0 }}
                />
                <Icon size={18} style={{ color: c.color, flexShrink: 0, marginTop: 'var(--vn-space-1)' }} aria-hidden />
              </div>
            </CardBody>
          </Card>
        );
      })}
    </div>
  );
}
